<template>
  <div class="d-flex align-items-center flex-column">
    <AutoComplete
      v-model="customerId"
      placeholder="Search accounts..."
      class="full-width"
      :options="options"
      name="customerId"
      :label="name"
      :validation-errors="v$.$errors"
      ignore-filter
      @search="handleSearch"
    />
    <Transition>
      <div class="full-width mt-3">
        <CustomerPreview
          v-if="customer"
          :customer="customer"
          :compare-customer="compareCustomer"
          :type="type"
        />
      </div>
    </Transition>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import { mapActions, mapState } from "vuex";

import AutoComplete from "@/components/AutoComplete.vue";
import Transition from "@/components/Transition.vue";
import { isSearchableTerm } from "@/lib/customerSearchTerm";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import CustomerPreview from "./CustomerPreview.vue";

export default {
  components: {
    AutoComplete,
    Transition,
    CustomerPreview
  },
  props: {
    filterId: {
      type: [String, Number],
      default: null
    },
    name: {
      type: String,
      default: ""
    },
    modelValue: {
      type: [String, Number],
      default: null
    },
    type: {
      type: String,
      default: ""
    }
  },
  emits: ["update:modelValue"],
  data() {
    return {
      customers: [],
      customerId: null
    };
  },
  computed: {
    ...mapState({
      mergeAccounts: "mergeAccounts",
      customer({ mergeAccounts }) {
        return mergeAccounts[this.type].customer;
      },
      compareCustomer({ mergeAccounts }) {
        if (this.type === "destination") {
          return mergeAccounts.duplicate.customer;
        }
        return mergeAccounts.destination.customer;
      }
    }),
    options() {
      return this.filterOptions(this.customers, this.filterId);
    }
  },
  watch: {
    customerId(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit("update:modelValue", newValue);
        if (newValue) {
          this.fetchCustomerData();
        }
      }
    },
    modelValue(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.customerId = newValue;
      }
    },
    mergeAccounts(newValue, oldValue) {
      // Handle account swap
      if (newValue !== oldValue) {
        this.customers = newValue[this.type].options;
      }
    }
  },
  created() {
    if (this.modelValue) {
      this.customerId = this.modelValue;
    }
  },
  mounted() {
    if (this.customerId) {
      this.fetchCustomerData();
    }
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      customerId: { required, $autoDirty: true }
    };
  },
  methods: {
    ...mapActions(["lookupCustomer", "fetchUser", "setMergeAccounts"]),
    fetchCustomerData() {
      this.fetchUser({ id: this.customerId, returnOnly: true }).then((res) => {
        this.setMergeAccounts({
          type: this.type,
          data: {
            customer: res.data,
            options: this.customers
          }
        });
      });
    },
    filterOptions(options, id) {
      return options
        .filter((user) => user.id !== id)
        .map((user) => ({
          value: user.id,
          label: `${this.fullName(user)} | ${user.email}`
        }));
    },
    handleSearch: debounce(function (terms = "") {
      if (isSearchableTerm(terms)) {
        //lookup customers via api
        this.lookupCustomer({ terms }).then((res) => {
          this.customers = res.data || [];
        });
      } else if (this.customers.length) {
        this.customers = [];
      }
    }, 300)
  }
};
</script>

<style lang="scss" scoped></style>
